/**
 * TASK-831 — the house view an advisor is asked to act on, readable at last.
 *
 * There was **no CIO surface anywhere in the product**: no tab, no widget, no hook, no
 * service. The frontend's entire knowledge of the house view was a single
 * `cio_view: string | null` leaking through `portfolioFitService`'s holdings, plus
 * `source: 'cio'` on a Radar row.
 *
 * Meanwhile the house view drives signals the advisor is expected to act on: `cio_stale_sell`
 * fires when the CIO flips a held instrument to SELL, and **every swap candidate the system
 * proposes is a CIO BUY**. The advisor was asked to act on a view they could not read.
 *
 * Two things this widget must get right, both of them about honesty rather than data:
 *
 *   * **the vintage** — a stale snapshot says so, using the same verdict the radar evaluator
 *     uses to decide whether CIO subjects may be cleared. Two definitions of "stale house
 *     view" would be TASK-828's defect in a different place;
 *   * **coverage** — an unrated holding is shown as *unrated*, never omitted. The universe
 *     does not cover everything a client holds, and a list that quietly drops those lets the
 *     advisor conclude the house has a view on their whole book. That is the same class of
 *     overclaim as TASK-818's silent slice.
 */

import { useState } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

import { WidgetContainer } from '../WidgetContainer';
import { useCioHoldings, useCioVintage } from '../../../hooks/useCio';
import type { CioHolding } from '../../../services/cioService';
import type { SignalScope } from '../../../types/signalScope';

const chf = (v: number | null) =>
  v == null ? '—' : `CHF ${Math.round(v).toLocaleString('de-CH').replace(/,/g, "'")}`;

const RATING_STYLE: Record<string, string> = {
  BUY: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  HOLD: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  SELL: 'bg-destructive/10 text-destructive border-destructive/30',
};

type Filter = 'all' | 'sell' | 'unrated';

function HoldingRow({ h, focused }: { h: CioHolding; focused: boolean }) {
  return (
    <div
      data-testid={focused ? 'cio-focused-holding' : 'cio-holding'}
      className={`flex items-start gap-3 border-b border-border py-2 last:border-0 ${
        focused ? '-mx-2 rounded bg-primary/5 px-2 ring-1 ring-primary' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">
          {focused && (
            <span className="mr-1 rounded bg-primary px-1 py-0.5 text-[9px] font-semibold text-primary-foreground">
              This signal
            </span>
          )}
          {h.security_name ?? h.issuer ?? h.isin}
        </p>
        <p className="text-[11px] text-muted-foreground">{h.isin}</p>
        {/* The house's own reasoning, on every rated row. It exists and was reachable nowhere. */}
        {h.cio_view && (
          <p data-testid="cio-rationale" className="mt-0.5 text-[11px] text-muted-foreground">
            {h.cio_view}
          </p>
        )}
      </div>
      <div className="shrink-0 text-right">
        {h.rated ? (
          <span
            data-testid="cio-rating"
            className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
              RATING_STYLE[h.rating ?? ''] ?? 'border-border bg-accent text-muted-foreground'
            }`}
          >
            {h.rating}
          </span>
        ) : (
          // NOT a dash. "The house has no view on this" is a fact about coverage, and an
          // advisor who cannot see it will assume the opposite.
          <span
            data-testid="cio-unrated"
            className="inline-block rounded border border-border bg-accent px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
            title="Outside the CIO universe — the house has no view on this holding."
          >
            not rated
          </span>
        )}
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{chf(h.market_value)}</p>
        {h.rating_since && (
          <p className="text-[10px] text-muted-foreground/70">since {h.rating_since.slice(0, 10)}</p>
        )}
      </div>
    </div>
  );
}

export function CIOHouseView({ clientId, focusIsin }: SignalScope) {
  const vintage = useCioVintage();
  const holdings = useCioHoldings(clientId);
  // Local, deliberately: which slice the advisor is looking at is not worth a URL param
  // here — the TAB is the addressable thing (TASK-726), and the filter is a lens on it.
  const [filter, setFilter] = useState<Filter>('all');

  const all = holdings.data?.holdings ?? [];
  const rows =
    filter === 'sell' ? all.filter((h) => h.rating === 'SELL')
    : filter === 'unrated' ? all.filter((h) => !h.rated)
    : all;

  const v = vintage.data;
  const stale = v?.status === 'stale' || v?.status === 'unavailable';

  return (
    <WidgetContainer
      title="CIO house view"
      source="CIO · recommendation universe"
      // TASK-825/831: the SNAPSHOT's date, not the tile's mount clock. A house view without
      // its vintage is exactly the failure TASK-750 fixed on the backend, re-created in the UI.
      dataAsOf={v?.as_of ?? null}
    >
      {/* Vintage header — the advisor deserves the same fact the evaluator acts on. */}
      {v && (
        <div
          data-testid="cio-vintage"
          className={`mb-3 rounded-md border px-3 py-2 text-[12px] ${
            stale
              ? 'border-amber-500/30 bg-amber-500/10 text-amber-800'
              : 'border-border bg-accent/40 text-muted-foreground'
          }`}
        >
          {v.status === 'unavailable' ? (
            <span className="inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              No CIO snapshot is loaded — there is no house view to read.
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              {stale && <AlertTriangle className="h-3.5 w-3.5" />}
              Snapshot {v.as_of?.slice(0, 10)} · {v.age_days} days old
              {stale && (
                <strong data-testid="cio-stale" className="ml-1">
                  — too old to be treated as the current house view
                </strong>
              )}
            </span>
          )}
        </div>
      )}

      {holdings.isLoading && <div className="h-24 w-full animate-pulse rounded bg-accent" />}
      {holdings.isError && (
        <p className="text-[13px] text-muted-foreground">Could not load the house view.</p>
      )}

      {holdings.data && (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
            {(['all', 'sell', 'unrated'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`cio-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-2 py-0.5 font-medium ${
                  filter === f ? 'bg-primary text-primary-foreground' : 'bg-accent text-muted-foreground'
                }`}
              >
                {f === 'all' ? `All ${all.length}`
                  : f === 'sell' ? `SELL ${all.filter((h) => h.rating === 'SELL').length}`
                  : `Not rated ${holdings.data.unrated_count}`}
              </button>
            ))}
          </div>

          {/* Coverage, stated rather than implied. */}
          {holdings.data.unrated_count > 0 && (
            <p data-testid="cio-coverage-gap" className="mb-2 text-[11px] text-muted-foreground">
              <ShieldAlert className="mr-1 inline h-3 w-3" />
              {holdings.data.unrated_count} of {all.length} holdings are outside the CIO
              universe — the house has no view on them.
            </p>
          )}

          {rows.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">Nothing in this view.</p>
          ) : (
            rows.map((h) => (
              <HoldingRow key={h.isin} h={h} focused={Boolean(focusIsin && h.isin === focusIsin)} />
            ))
          )}
        </>
      )}
    </WidgetContainer>
  );
}

export default CIOHouseView;
